import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { checkmarkSharp, closeCircleSharp } from 'ionicons/icons'
import { IOption, IOptionWithKey, IOptions, IOptionsWithKey } from '../../../../common/common.types'
import { setBodySelectionMode, setSelectedExam, setShowExamModal } from '../store/bodyAssessment.actions'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { BodyAssessmentConstants } from '../store/bodyAssessment.constants'
import { useTranslation } from 'react-i18next'
import { IonIcon, IonItem, IonLabel, IonList } from '@ionic/react'
import { getBodyAssessmentSelector } from '../selectors'
import { compareObjectsIfEqual } from '../../../../utilities/object.util'
import { initialDrainsAndTubesState } from '../drainsTubes/store/drainsAndTubes.reducers'
import { initialObstetricsState } from '../obstetrics/store/obstetrics.reducers'
import { ITripInformationState } from '../../../tripInformation/store/tripInformation.types'
import { getDrainsAndTubesSelector } from '../drainsTubes/selectors'
import { getObstetricsSelector } from '../obstetrics/selectors'
import { getPatientDemographicsSelector } from '../../../patientDemographics/selectors'
import { getTripInformationSelector } from '../../../tripInformation/selectors'
import { resetObstetrics, saveObstetrics } from '../obstetrics/store/obstetrics.actions'
import { resetDrainsAndTubes } from '../drainsTubes/store/drainsAndTubes.actions'
import { IConfigurationsState } from '../../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../../configurations/selectors'
import { resetBurnsTbsa, resetBurns, saveBurns, selectedBurnExam } from '../burns/store/burns.actions'
import {
  initialBurnsTbsaState,
  initialBurnsState,
  initialAdditionalBurnInformation,
} from '../burns/store/burns.reducers'
import { getBurnsSelector } from '../burns/selectors'
import {
  deleteBodyLocationFinding,
  setChestLungMode,
  setDocumentFindings,
  setEnabledZoom,
  setExaminationInProgress,
  setSelectedBodyPartData,
  setZoomedBodyPart,
} from '../bodySurvey/store/bodySurvey.actions'
import { getBodySurveySelector } from '../bodySurvey/selectors'
import { examMapping, getDocumentedLocations } from '../../../../utilities/bodySurvey.util'
import frontBodyData from '../bodySurvey/data/adultBodyFront.json'
import { IBodyParts } from '../bodySurvey/store/bodySurvey.types'
import { ObstetricsConstants } from '../obstetrics/store/obstetrics.constants'
import { DrainsAndTubesConstants } from '../drainsTubes/store/drainsAndTubes.constants'
import { clearValidationErrorsByPathList } from '../../../../utilities/runRulesValidation.util'
import { getRulesSelector } from '../../../rules/selectors'
import { IRulesState } from '../../../rules/store/rules.types'

interface IExamsPicklistContent {
  dataExists?: boolean
}

const ExamsPicklistContent: React.FC<IExamsPicklistContent> = ({ dataExists }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const tripInformationState: ITripInformationState = useSelector(getTripInformationSelector)
  const configuration: IConfigurationsState = useSelector(getConfigurationsSelector)
  const rules: IRulesState = useSelector(getRulesSelector)

  const { selectedExam, showExamModal } = useSelector(getBodyAssessmentSelector)
  const drainsAndTubesState = useSelector(getDrainsAndTubesSelector)
  const burnsTbsaState = useSelector(getBurnsSelector)
  const obstetricsState = useSelector(getObstetricsSelector)
  const bodySurveyState = useSelector(getBodySurveySelector)
  const patientDemographics = useSelector(getPatientDemographicsSelector)
  const { staffingLevel } = tripInformationState
  const { datapointsConfig } = configuration

  const { DP_GENERAL_EXTREMITIES, DP_HIDE_FIELDS_BY_STAFF, DP_INTRATHORACIC } = datapointsConfig
  const showDrainsAndTubes = staffingLevel?.isAls === 1 || DP_HIDE_FIELDS_BY_STAFF === 0

  const hideGeneralExtremitiesConfig = DP_GENERAL_EXTREMITIES === 0
  const hideChestLungConfig = DP_INTRATHORACIC === 0

  const burnsTbsaData = {
    burnsExamData: burnsTbsaState.burnsExamData,
    isTBSA: burnsTbsaState.isTBSA,
  }
  const { examValues, examValuesPNNVs, findings, __documentFindings } = bodySurveyState
  const otherExamsIds = [6, 12, 13]
  const posteriorViewIds = [101, 102, 106]

  const combinedExamOptions = [...examValues, ...examValuesPNNVs]
  const allDocumentedLocations = getDocumentedLocations(combinedExamOptions, findings)

  const filterBodySurveyData = allDocumentedLocations.filter((loc) => loc.id && !otherExamsIds.includes(loc.id))

  const allDocumentedLocationIds = getDocumentedLocations(combinedExamOptions, findings).map((location) => location.id)
  const isPosteriorViewDataPresent = posteriorViewIds.some((posteriorId) =>
    allDocumentedLocationIds.includes(posteriorId)
  )
  const isGeneralExtremitiesDataPresent = allDocumentedLocationIds.includes(10)

  let filteredData: IOptions = [...filterBodySurveyData]
  if (hideGeneralExtremitiesConfig && !isGeneralExtremitiesDataPresent) {
    filteredData = filterBodySurveyData.filter((loc) => loc.id !== 10)
  }

  if (hideChestLungConfig && !isPosteriorViewDataPresent) {
    filteredData = filterBodySurveyData.filter((loc) => loc?.id && !posteriorViewIds.includes(loc?.id))
  }

  const examsListOptions: IOptionsWithKey = [
    {
      key: BodyAssessmentConstants.BODY_SURVEY,
      name: t('additionalAssessments.examTypes.BodySurvey'),
    },
    {
      key: BodyAssessmentConstants.BURNS_RULE_OF_NINES,
      name: t('additionalAssessments.examTypes.BurnsRuleOfNines'),
      initialState: { ...initialBurnsTbsaState, burnsExamData: initialBurnsState.burnsExamData },
      currentState: burnsTbsaData,
    },
    {
      key: BodyAssessmentConstants.BURNS_TBSA,
      name: t('additionalAssessments.examTypes.BurnsTbsa'),
      initialState: { ...initialBurnsTbsaState, burnsExamData: initialBurnsState.burnsExamData },
      currentState: burnsTbsaData,
    },
    {
      key: BodyAssessmentConstants.NEURO_CONDITIONS,
      name: t('additionalAssessments.examTypes.NeuroConditions'),
    },
    {
      key: BodyAssessmentConstants.MENTAL_CONDITIONS,
      name: t('additionalAssessments.examTypes.MentalConditions'),
    },
    {
      key: BodyAssessmentConstants.CARDIO_CONDITIONS,
      name: t('additionalAssessments.examTypes.CardioConditions'),
    },
    {
      key: BodyAssessmentConstants.DRAINS_TUBES,
      name: t('additionalAssessments.examTypes.DrainsTubes'),
      hidden: !showDrainsAndTubes,
      initialState: initialDrainsAndTubesState,
      currentState: drainsAndTubesState,
    },
    {
      key: BodyAssessmentConstants.OBSTETRICS,
      name: t('additionalAssessments.examTypes.Obstetrics'),
      hidden: patientDemographics?.gender?.name === 'Male' ? true : false,
      initialState: initialObstetricsState,
      currentState: obstetricsState,
    },
  ]

  const handleDeleteBurns = (currentExam: string) => {
    const updatedTBSA = burnsTbsaState.isTBSA === 1 ? 0 : 1
    dispatch(resetBurnsTbsa())
    setTimeout(() => {
      dispatch(selectedBurnExam(updatedTBSA))
      dispatch(setSelectedExam(currentExam))
      if (showExamModal) {
        dispatch(setShowExamModal(false))
      }
    }, 100)
  }

  const handleBurnsSelection = (currentExam: string) => {
    let currentSelectedSection = ''
    if (burnsTbsaState.isTBSA === 1 && currentExam === BodyAssessmentConstants.BURNS_RULE_OF_NINES) {
      currentSelectedSection = t('additionalAssessments.examTypes.BurnsRuleOfNines')
    } else if (burnsTbsaState.isTBSA === 0 && currentExam === BodyAssessmentConstants.BURNS_TBSA) {
      currentSelectedSection = t('additionalAssessments.examTypes.BurnsTbsa')
    } else {
      dispatch(setSelectedExam(currentExam))
      if (showExamModal) {
        dispatch(setShowExamModal(false))
      }
    }

    if (currentSelectedSection.length) {
      const alertState = {
        header: currentSelectedSection,
        message: t('burns.deleteBurns.message'),
        buttons: [
          { text: 'Cancel' },
          { text: 'Ok', role: 'destructive', handler: () => handleDeleteBurns(currentExam) },
        ],
      }
      dispatch(showAlert(alertState))
    }
  }

  const handleClearExam = (selectedExam: string) => {
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY) {
      dispatch(deleteBodyLocationFinding(filterBodySurveyData))
    } else {
      const selectedId = examMapping[selectedExam]?.[0]?.id
      const filteredData = allDocumentedLocations.filter((item) => selectedId === item.id)
      dispatch(deleteBodyLocationFinding(filteredData))
    }
  }

  const handleExamSelection = (currentExam: string) => {
    if (
      currentExam === BodyAssessmentConstants.BURNS_TBSA ||
      currentExam === BodyAssessmentConstants.BURNS_RULE_OF_NINES
    ) {
      handleBurnsSelection(currentExam)
    } else {
      dispatch(setSelectedExam(currentExam))
      if (showExamModal) {
        dispatch(setShowExamModal(false))
      }
    }
    dispatch(setExaminationInProgress(false))
  }

  const handleSelection = (exam: IOption, selectedExam: string) => {
    if (!Array.isArray(frontBodyData) || !exam || typeof exam.id !== 'number') {
      return
    }
    const examId = exam.id

    const filterData = frontBodyData
      .filter((body: IBodyParts) => Array.isArray(body.childrenIds) && body.childrenIds.includes(examId))
      .map((body: IBodyParts) => ({
        ...body,
        hasBack: [101, 102, 106].includes(examId),
      }))

    if (filterData.length > 0 && filterData[0].hasZoom) {
      dispatch(setZoomedBodyPart(filterData[0]))
      dispatch(setEnabledZoom(true))
      if (filterData[0].hasBack) {
        dispatch(setChestLungMode({ id: 2, name: 'Back' }))
      } else {
        dispatch(setChestLungMode({ id: 1, name: 'Front' }))
      }
    } else {
      dispatch(setEnabledZoom(false))
    }
    if (exam.name === t('additionalAssessments.bodySurvey.generalExtremities')) {
      dispatch(setBodySelectionMode({ id: 3, name: 'General Extremities', hide: false }))
    } else if (exam.name === t('additionalAssessments.bodySurvey.externalSkin')) {
      dispatch(setBodySelectionMode({ id: 2, name: 'External/Skin' }))
    } else {
      dispatch(setBodySelectionMode({ id: 1, name: 'Body parts' }))
    }
    dispatch(setDocumentFindings(false))
    dispatch(setSelectedBodyPartData([exam]))
    dispatch(setShowExamModal(false))
    handleExamSelection(selectedExam)
  }

  const handleRemoveExamFindings = (examName: string | undefined) => {
    const alertState = {
      header: t('additionalAssessments.clearExamFindings.header'),
      message: t('additionalAssessments.clearExamFindings.message') + examName + ' ?',
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: () => handleDeleteExam(examName) }],
    }
    dispatch(showAlert(alertState))
  }

  const handleDeleteExam = (examName: string | undefined) => {
    if (examName === t('additionalAssessments.examTypes.Obstetrics')) {
      rules.errors = clearValidationErrorsByPathList(rules.errors, [ObstetricsConstants.SET_COMMENTS])
      dispatch(resetObstetrics())
      dispatch(saveObstetrics())
    }
    if (examName === t('additionalAssessments.examTypes.DrainsTubes')) {
      rules.errors = clearValidationErrorsByPathList(rules.errors, [DrainsAndTubesConstants.SET_COMMENTS_OTHER])
      dispatch(resetDrainsAndTubes())
    }
    if (examName === t('additionalAssessments.examTypes.BurnsTbsa')) {
      dispatch(resetBurns())
      dispatch(selectedBurnExam(1))
      dispatch(saveBurns())
    }
    if (examName === t('additionalAssessments.examTypes.BurnsRuleOfNines')) {
      dispatch(resetBurns())
      dispatch(selectedBurnExam(0))
      dispatch(saveBurns())
    }
    if (examName === t('additionalAssessments.examTypes.BodySurvey')) {
      handleClearExam(BodyAssessmentConstants.BODY_SURVEY)
    }
    if (examName === t('additionalAssessments.examTypes.NeuroConditions')) {
      handleClearExam(BodyAssessmentConstants.NEURO_CONDITIONS)
    }
    if (examName === t('additionalAssessments.examTypes.MentalConditions')) {
      handleClearExam(BodyAssessmentConstants.MENTAL_CONDITIONS)
    }
    if (examName === t('additionalAssessments.examTypes.CardioConditions')) {
      handleClearExam(BodyAssessmentConstants.CARDIO_CONDITIONS)
    }
  }

  const handleRemoveExamLocation = (exam: IOption) => {
    const alertState = {
      header: t('additionalAssessments.clearExamLocation.header'),
      message: t('additionalAssessments.clearExamLocation.message') + exam.name + ' ?',
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: () => handleDeleteExamLocation(exam) }],
    }
    dispatch(showAlert(alertState))
  }

  const handleDeleteExamLocation = (exam: IOption) => {
    if (selectedExam === BodyAssessmentConstants.BODY_SURVEY) {
      dispatch(setShowExamModal(false))
    }
    dispatch(deleteBodyLocationFinding(exam))
    if (__documentFindings) {
      dispatch(setDocumentFindings(false))
    }
  }

  const madeFindingsOnBodySurveyExams = (exam: IOptionWithKey) => {
    let bool = false
    if (exam.key === BodyAssessmentConstants.BODY_SURVEY) {
      bool = filterBodySurveyData.length > 0
    } else {
      const locationId = examMapping[exam.key]?.[0]?.id
      bool = allDocumentedLocations.some((item) => locationId === item.id)
    }
    return bool
  }

  const madeFindingsOnExam = (exam: IOptionWithKey) => {
    const key = exam.key
    const additionalBurninformationData: any = burnsTbsaState.additionalBurnInformation
    switch (key) {
      case BodyAssessmentConstants.BURNS_TBSA:
        return (
          burnsTbsaState.isTBSA === 1 &&
          (burnsTbsaState.burnsExamData.length !== 0 ||
            !compareObjectsIfEqual(initialAdditionalBurnInformation, additionalBurninformationData))
        )
      case BodyAssessmentConstants.BURNS_RULE_OF_NINES:
        return (
          burnsTbsaState.isTBSA === 0 &&
          (burnsTbsaState.burnsExamData.length !== 0 ||
            !compareObjectsIfEqual(initialAdditionalBurnInformation, additionalBurninformationData))
        )
      case BodyAssessmentConstants.BODY_SURVEY:
      case BodyAssessmentConstants.MENTAL_CONDITIONS:
      case BodyAssessmentConstants.NEURO_CONDITIONS:
      case BodyAssessmentConstants.CARDIO_CONDITIONS:
        return madeFindingsOnBodySurveyExams(exam)
      default:
        if (exam.initialState && exam.currentState) {
          return !compareObjectsIfEqual(exam.initialState, exam.currentState)
        }
        return true
    }
  }

  return (
    <IonList className='exams-picklist-modal-content'>
      {examsListOptions.map(
        (exam) =>
          !exam.hidden && (
            <div key={exam.key}>
              <IonItem
                lines='none'
                onClick={() => handleExamSelection(exam.key)}
                className={`${madeFindingsOnExam(exam) ? 'exam-with-findings' : ''} ${
                  selectedExam === exam.key ? 'active-exam-screen' : ''
                }`}>
                <IonLabel className='d-flex align-items-center'>
                  <div className='d-flex align-items-center w-100'>
                    <h5 className='display-inline'>{exam.name}</h5>
                    {madeFindingsOnExam(exam) && (
                      <IonIcon color='success' className='checkmark-icon ml-10' icon={checkmarkSharp} />
                    )}
                  </div>
                  {madeFindingsOnExam(exam) && (
                    <IonIcon
                      color='neutral-base'
                      icon={closeCircleSharp}
                      className='cross'
                      onClick={() => handleRemoveExamFindings(exam.name)}
                    />
                  )}
                </IonLabel>
              </IonItem>
              {exam.key === BodyAssessmentConstants.BODY_SURVEY && (
                <ul className='exam-location-bullet-list'>
                  {filteredData.length > 0 &&
                    filteredData.map((data) => (
                      <li key={data.id} className='exam-location-list'>
                        <div className='d-flex'>
                          <IonLabel color='primary' onClick={() => handleSelection(data, exam.key)}>
                            {data.name}
                          </IonLabel>
                          <IonIcon
                            color='neutral-base'
                            icon={closeCircleSharp}
                            className='cross mr-1'
                            onClick={() => handleRemoveExamLocation(data)}
                          />
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )
      )}
    </IonList>
  )
}

export default ExamsPicklistContent
